import React, { useState, useRef, useEffect } from "react";
import {
  Check,
  Copy,
  Terminal,
  Key,
  Download,
  Shield,
  Play,
  CheckCircle2,
  ChevronDown,
  AlertTriangle,
  Zap,
  BookOpen,
  Rocket,
} from "lucide-react";

/* ═══════════════════════════════════════
   CODE SNIPPETS
   ═══════════════════════════════════════ */

const QUICK_START = `pip install scopuz-cli
scopuz analyze https://yoursite.com`;

const BACKEND_SETUP = `cd backend
python3 -m pip install -r requirements.txt
python3 server.py`;

const EDITABLE_INSTALL = `cd cli
python3 -m pip install -e .`;

const AUTH_CMD = (key) => `scopuz auth ${key || "<YOUR_API_KEY>"}`;

const ANALYZE_EXAMPLES = `scopuz analyze https://yoursite.com/page
scopuz analyze https://yoursite.com/page --query "best b2b crm software"`;

const VERIFY_COMMANDS = `scopuz --help
scopuz analyze --help`;

/* ═══════════════════════════════════════
   CODE BLOCK — matches app design system
   ═══════════════════════════════════════ */

function CodeBlock({ label, code, id, copiedId, onCopy }) {
  const isCopied = copiedId === id;

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
    >
      {/* Header bar */}
      <div
        className="flex items-center justify-between px-4 py-2"
        style={{ background: "var(--surface-2)", borderBottom: "1px solid var(--border)" }}
      >
        <span
          className="text-[10px] font-semibold uppercase tracking-widest"
          style={{ color: "var(--text-subtle)", fontFamily: "'JetBrains Mono', monospace" }}
        >
          {label}
        </span>
        <button
          type="button"
          onClick={() => onCopy(id, code)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all duration-200"
          style={{
            background: isCopied ? "var(--primary-light)" : "transparent",
            border: isCopied
              ? "1px solid rgba(163,230,53,0.3)"
              : "1px solid var(--border)",
            color: isCopied ? "var(--primary)" : "var(--text-muted)",
          }}
        >
          {isCopied ? (
            <Check className="w-3 h-3" />
          ) : (
            <Copy className="w-3 h-3" />
          )}
          {isCopied ? "Copied" : "Copy"}
        </button>
      </div>
      {/* Code body */}
      <pre
        className="px-4 py-3 text-[13px] leading-relaxed overflow-x-auto m-0"
        style={{
          color: "var(--primary)",
          fontFamily: "'JetBrains Mono', monospace",
          whiteSpace: "pre",
        }}
      >
        {code}
      </pre>
    </div>
  );
}

/* ═══════════════════════════════════════
   ACCORDION ITEM — troubleshooting
   ═══════════════════════════════════════ */

function TroubleshootItem({ title, fix, children, isOpen, onToggle }) {
  const bodyRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (bodyRef.current) setHeight(bodyRef.current.scrollHeight);
  }, [isOpen]);

  return (
    <div
      className="rounded-xl transition-all duration-200"
      style={{
        background: isOpen ? "var(--surface-2)" : "var(--surface)",
        border: isOpen
          ? "1px solid rgba(163,230,53,0.18)"
          : "1px solid var(--border)",
      }}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3.5 text-left text-sm font-semibold"
        style={{ color: "var(--text)" }}
      >
        <span className="flex items-center gap-2">
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: isOpen ? "var(--primary)" : "var(--text-subtle)" }}
          />
          <code style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "12px" }}>
            {title}
          </code>
        </span>
        <ChevronDown
          className="w-4 h-4 transition-transform duration-200"
          style={{
            color: isOpen ? "var(--primary)" : "var(--text-subtle)",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </button>
      <div
        style={{
          maxHeight: isOpen ? height + "px" : "0px",
          overflow: "hidden",
          transition: "max-height 0.25s ease",
        }}
      >
        <div
          ref={bodyRef}
          className="px-4 pb-4 text-xs leading-relaxed"
          style={{ color: "var(--text-muted)", borderTop: "1px solid var(--border)" }}
        >
          <div className="pt-3">{children}</div>
          {fix && (
            <div
              className="mt-2.5 rounded-lg px-3 py-2 text-xs"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                color: "var(--primary)",
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              $ {fix}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   TERMINAL PREVIEW — macOS-style window
   ═══════════════════════════════════════ */

function TerminalPreview({ apiKey }) {
  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: "#000",
        border: "1px solid var(--border)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
      }}
    >
      {/* macOS title bar */}
      <div
        className="flex items-center justify-between px-4 py-2.5"
        style={{ background: "var(--surface)", borderBottom: "1px solid var(--border)" }}
      >
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#f87171" }} />
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#fbbf24" }} />
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--primary)" }} />
        </div>
        <span
          className="text-[10px] font-medium flex items-center gap-1"
          style={{ color: "var(--text-subtle)", fontFamily: "'JetBrains Mono', monospace" }}
        >
          <Terminal className="w-3 h-3" style={{ color: "var(--primary)" }} />
          scopuz-cli
        </span>
        <div className="w-10" />
      </div>

      {/* Terminal body */}
      <div
        className="p-4 text-xs sm:text-[13px] leading-relaxed overflow-x-auto"
        style={{ fontFamily: "'JetBrains Mono', monospace", maxHeight: 400, overflowY: "auto" }}
      >
        {/* Auth line */}
        <div className="mb-3">
          <span style={{ color: "var(--primary)" }}>user@dev</span>
          <span style={{ color: "var(--text-subtle)" }}>:</span>
          <span style={{ color: "#38bdf8" }}>~</span>
          <span style={{ color: "var(--text)" }}> $ scopuz auth {apiKey || "<API_KEY>"}</span>
        </div>
        <div className="mb-4" style={{ color: "var(--primary)" }}>
          ✓ Authorized. Token saved to ~/.scopuz/config.json
        </div>

        {/* Analyze line */}
        <div className="mb-3">
          <span style={{ color: "var(--primary)" }}>user@dev</span>
          <span style={{ color: "var(--text-subtle)" }}>:</span>
          <span style={{ color: "#38bdf8" }}>~</span>
          <span style={{ color: "var(--text)" }}> $ scopuz analyze https://site.com</span>
        </div>
        <div className="mb-1" style={{ color: "var(--text-muted)" }}>
          ➜ Fetching HTML source…
        </div>
        <div className="mb-1" style={{ color: "var(--text-muted)" }}>
          ➜ Running AI engine evaluation…
        </div>
        <div className="mb-4" style={{ color: "var(--text)" }}>
          ✓ Analysis complete
        </div>

        {/* Scorecard */}
        <div
          className="rounded-xl p-4 space-y-3"
          style={{
            background: "rgba(163,230,53,0.04)",
            border: "1px solid rgba(163,230,53,0.12)",
          }}
        >
          <div
            className="flex items-center justify-between pb-2 text-[10px] uppercase font-bold tracking-widest"
            style={{ borderBottom: "1px solid rgba(163,230,53,0.1)" }}
          >
            <span style={{ color: "var(--primary)" }}>AI Visibility Scorecard</span>
            <span style={{ color: "var(--text-subtle)" }}>v0.1.0</span>
          </div>

          {/* Metrics row */}
          <div className="grid grid-cols-2 gap-3">
            <div
              className="rounded-lg p-2.5"
              style={{ background: "rgba(0,0,0,0.4)", border: "1px solid var(--border)" }}
            >
              <div className="text-[9px] uppercase tracking-wider font-bold mb-0.5" style={{ color: "var(--text-subtle)" }}>
                Score
              </div>
              <div className="text-2xl font-bold" style={{ color: "var(--primary)" }}>
                78<span className="text-[10px] font-normal" style={{ color: "var(--text-subtle)" }}>/100</span>
              </div>
            </div>
            <div
              className="rounded-lg p-2.5"
              style={{ background: "rgba(0,0,0,0.4)", border: "1px solid var(--border)" }}
            >
              <div className="text-[9px] uppercase tracking-wider font-bold mb-0.5" style={{ color: "var(--text-subtle)" }}>
                Citation
              </div>
              <div className="text-2xl font-bold" style={{ color: "#38bdf8" }}>64%</div>
            </div>
          </div>

          {/* Engine list */}
          <div
            className="rounded-lg p-2.5 space-y-1.5 text-xs"
            style={{ background: "rgba(0,0,0,0.4)", border: "1px solid var(--border)" }}
          >
            <div className="text-[9px] uppercase tracking-wider font-bold mb-1" style={{ color: "var(--text-subtle)" }}>
              Engines
            </div>
            {[
              ["ChatGPT", 82],
              ["Perplexity", 74],
              ["Gemini", 71],
              ["Copilot", 68],
            ].map(([name, score]) => (
              <div key={name} className="flex items-center justify-between">
                <span className="flex items-center gap-1.5" style={{ color: "var(--text)" }}>
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: "var(--primary)" }}
                  />
                  {name}
                </span>
                <div className="flex items-center gap-2">
                  <div
                    className="w-16 h-1 rounded-full overflow-hidden"
                    style={{ background: "var(--border)" }}
                  >
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${score}%`,
                        background: "var(--primary)",
                        transition: "width 0.6s ease",
                      }}
                    />
                  </div>
                  <span style={{ color: "var(--text-muted)", minWidth: 20, textAlign: "right" }}>
                    {score}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Recommendations */}
          <div
            className="pt-2 text-[11px] space-y-1"
            style={{ borderTop: "1px solid rgba(163,230,53,0.08)" }}
          >
            <div className="text-[9px] uppercase tracking-wider font-bold" style={{ color: "var(--primary)" }}>
              Actions
            </div>
            <div style={{ color: "var(--text-muted)" }}>• Add FAQ schema markup</div>
            <div style={{ color: "var(--text-muted)" }}>• Improve heading hierarchy</div>
            <div style={{ color: "var(--text-muted)" }}>• Reference authoritative domains</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   STEP — numbered setup step
   ═══════════════════════════════════════ */

const STEP_ICONS = [null, Terminal, Download, Shield, Play, CheckCircle2];

function Step({ num, title, desc, children, isLast }) {
  const Icon = STEP_ICONS[num] || Terminal;
  return (
    <div className="relative flex gap-4">
      {/* Vertical line */}
      {!isLast && (
        <div
          className="absolute left-[17px] top-10 bottom-0 w-px"
          style={{ background: "var(--border)" }}
        />
      )}
      {/* Number circle */}
      <div
        className="relative z-10 flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold"
        style={{
          background: "var(--surface-2)",
          border: "1px solid var(--border)",
          color: "var(--text-muted)",
        }}
      >
        <Icon className="w-4 h-4" style={{ color: "var(--primary)" }} />
      </div>
      {/* Content */}
      <div className="flex-1 pb-8 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span
            className="text-[10px] font-bold uppercase tracking-widest"
            style={{ color: "var(--primary)" }}
          >
            Step {num}
          </span>
        </div>
        <h3
          className="text-sm font-semibold mb-1"
          style={{ color: "var(--text)" }}
        >
          {title}
        </h3>
        <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
          {desc}
        </p>
        {children}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════ */

export default function CLIPage() {
  const [copiedId, setCopiedId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [installMode, setInstallMode] = useState("pip"); // pip | editable
  const [openFaq, setOpenFaq] = useState(null);

  const handleCopy = async (id, text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement("textarea");
        el.value = text;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      setCopiedId(id);
      setTimeout(() => setCopiedId(""), 1600);
    } catch {
      setCopiedId("");
    }
  };

  return (
    <div className="space-y-8" data-testid="cli-page">

      {/* ── PAGE HEADER ────────────────────── */}
      <div>
        <h1 className="font-display text-3xl font-bold text-white mb-2">
          CLI{" "}
          <span className="bg-gradient-to-r from-primary to-primary-hover bg-clip-text text-transparent">
            Documentation
          </span>
        </h1>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          Setup, authenticate, and run AI visibility analysis from your terminal.
        </p>
      </div>

      {/* ── QUICK START BANNER ──────────────── */}
      <div
        className="action-card relative overflow-hidden"
        style={{ borderColor: "rgba(163,230,53,0.2)" }}
      >
        {/* Top glow line */}
        <div
          className="absolute top-0 left-0 right-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(163,230,53,0.25) 50%, transparent 100%)",
          }}
        />
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-4 h-4" style={{ color: "var(--primary)" }} />
          <span
            className="text-xs font-semibold uppercase tracking-widest"
            style={{ color: "var(--primary)" }}
          >
            Quick Start
          </span>
        </div>
        <CodeBlock
          label="Install & Run"
          code={QUICK_START}
          id="quick"
          copiedId={copiedId}
          onCopy={handleCopy}
        />
        <p className="text-xs mt-3" style={{ color: "var(--text-subtle)" }}>
          Works when your backend is already running and your API key is configured.
        </p>
      </div>

      {/* ── TWO-COLUMN LAYOUT ────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* LEFT — SETUP WALKTHROUGH ─────────── */}
        <div className="lg:col-span-7 space-y-6">

          {/* API Key Configurator */}
          <div className="metric-card">
            <div className="flex items-center gap-2 mb-3">
              <Key className="w-4 h-4" style={{ color: "var(--primary)" }} />
              <span className="text-xs font-semibold" style={{ color: "var(--text)" }}>
                API Key Configurator
              </span>
            </div>
            <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
              Paste your key here. The auth command below updates automatically.
            </p>
            <div className="relative">
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Paste your CLI API Key…"
                className="glass-input text-xs"
                style={{ paddingRight: apiKey ? "5.5rem" : undefined }}
              />
              {apiKey && (
                <span
                  className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-[10px] font-semibold"
                  style={{ color: "var(--primary)" }}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Configured
                </span>
              )}
            </div>
          </div>

          {/* Steps Section */}
          <div className="metric-card p-0 overflow-hidden">
            <div
              className="flex items-center justify-between px-5 py-4"
              style={{ borderBottom: "1px solid var(--border)" }}
            >
              <div className="flex items-center gap-2">
                <Rocket className="w-4 h-4" style={{ color: "var(--primary)" }} />
                <h2 className="text-sm font-semibold" style={{ color: "var(--text)" }}>
                  Setup Walkthrough
                </h2>
              </div>
              <span className="text-xs" style={{ color: "var(--text-subtle)" }}>
                5 steps
              </span>
            </div>

            <div className="px-5 py-5">
              {/* Step 1 */}
              <Step
                num={1}
                title="Start Local Backend"
                desc="Launch the Scopuz FastAPI server so the CLI can send analysis requests."
              >
                <CodeBlock
                  label="Backend"
                  code={BACKEND_SETUP}
                  id="backend"
                  copiedId={copiedId}
                  onCopy={handleCopy}
                />
                <p
                  className="text-[11px] mt-2 flex items-center gap-1"
                  style={{ color: "var(--text-subtle)" }}
                >
                  <span style={{ color: "var(--primary)" }}>→</span> Serves at{" "}
                  <code
                    className="font-mono"
                    style={{ color: "var(--text)", fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    http://localhost:8000
                  </code>
                </p>
              </Step>

              {/* Step 2 */}
              <Step
                num={2}
                title="Install the CLI"
                desc="Install the scopuz-cli package. Choose stable or local editable mode."
              >
                {/* Toggle */}
                <div
                  className="inline-flex rounded-lg p-0.5 mb-3"
                  style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
                >
                  {[
                    { key: "pip", label: "Pip Install" },
                    { key: "editable", label: "Editable (Dev)" },
                  ].map((m) => (
                    <button
                      key={m.key}
                      onClick={() => setInstallMode(m.key)}
                      className="px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all"
                      style={{
                        background:
                          installMode === m.key ? "var(--primary)" : "transparent",
                        color:
                          installMode === m.key ? "#000" : "var(--text-muted)",
                      }}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>

                <CodeBlock
                  label={installMode === "pip" ? "Pip Package" : "Editable Install"}
                  code={installMode === "pip" ? "pip install scopuz-cli" : EDITABLE_INSTALL}
                  id={installMode}
                  copiedId={copiedId}
                  onCopy={handleCopy}
                />
              </Step>

              {/* Step 3 */}
              <Step
                num={3}
                title="Authenticate"
                desc="Save your API key locally so the CLI can authorize requests."
              >
                <CodeBlock
                  label="Authentication"
                  code={AUTH_CMD(apiKey)}
                  id="auth"
                  copiedId={copiedId}
                  onCopy={handleCopy}
                />
                <p
                  className="text-[11px] mt-2 flex items-center gap-1.5"
                  style={{ color: "var(--text-subtle)" }}
                >
                  <BookOpen className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} />
                  <span>
                    Get your key from{" "}
                    <strong style={{ color: "var(--text)" }}>Profile</strong> →{" "}
                    <strong style={{ color: "var(--text)" }}>CLI Access</strong> →{" "}
                    <strong style={{ color: "var(--text)" }}>Generate New API Key</strong>
                  </span>
                </p>
              </Step>

              {/* Step 4 */}
              <Step
                num={4}
                title="Run Analysis"
                desc="Analyze any URL. Optionally pass a query to simulate a specific AI prompt."
              >
                <CodeBlock
                  label="Analyze Commands"
                  code={ANALYZE_EXAMPLES}
                  id="analyze"
                  copiedId={copiedId}
                  onCopy={handleCopy}
                />
              </Step>

              {/* Step 5 */}
              <Step
                num={5}
                title="Verify Installation"
                desc="Confirm the CLI is properly installed and review available commands."
                isLast
              >
                <CodeBlock
                  label="Verify"
                  code={VERIFY_COMMANDS}
                  id="verify"
                  copiedId={copiedId}
                  onCopy={handleCopy}
                />
              </Step>
            </div>
          </div>
        </div>

        {/* RIGHT — TERMINAL PREVIEW + SIDEBAR ── */}
        <div className="lg:col-span-5 space-y-6">

          {/* Terminal Preview */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Terminal className="w-4 h-4" style={{ color: "var(--primary)" }} />
              <span
                className="text-xs font-semibold uppercase tracking-widest"
                style={{ color: "var(--primary)" }}
              >
                Live Preview
              </span>
            </div>
            <TerminalPreview apiKey={apiKey} />
          </div>

          {/* Troubleshooting */}
          <div className="metric-card p-0 overflow-hidden">
            <div
              className="flex items-center gap-2 px-5 py-4"
              style={{ borderBottom: "1px solid var(--border)" }}
            >
              <AlertTriangle className="w-4 h-4" style={{ color: "#fbbf24" }} />
              <h3 className="text-sm font-semibold" style={{ color: "var(--text)" }}>
                Troubleshooting
              </h3>
            </div>
            <div className="p-3 space-y-2">
              <TroubleshootItem
                title="scopuz: command not found"
                fix="python3 -m scopuz_cli.main --help"
                isOpen={openFaq === 0}
                onToggle={() => setOpenFaq(openFaq === 0 ? null : 0)}
              >
                Your Python/pip bin folder isn't in <code>PATH</code>. Call the module
                directly or add <code>~/.local/bin</code> to your shell profile.
              </TroubleshootItem>

              <TroubleshootItem
                title="401 Invalid API key"
                fix="scopuz auth <NEW_KEY>"
                isOpen={openFaq === 1}
                onToggle={() => setOpenFaq(openFaq === 1 ? null : 1)}
              >
                Your token has expired or is invalid. Generate a fresh CLI API key from
                the Profile page and re-authenticate.
              </TroubleshootItem>

              <TroubleshootItem
                title="Connection refused"
                fix="curl -s http://localhost:8000/api/health"
                isOpen={openFaq === 2}
                onToggle={() => setOpenFaq(openFaq === 2 ? null : 2)}
              >
                The local backend isn't running. Make sure uvicorn is active on port 8000
                and the health endpoint responds.
              </TroubleshootItem>
            </div>
          </div>

          {/* Prerequisites */}
          <div className="metric-card">
            <h4
              className="text-xs font-semibold uppercase tracking-widest mb-3"
              style={{ color: "var(--text-subtle)" }}
            >
              Requirements
            </h4>
            <div className="grid grid-cols-2 gap-2.5">
              {[
                "Python 3.10+",
                "pip package manager",
                "Cloned repository",
                "CLI API Key",
              ].map((req) => (
                <div
                  key={req}
                  className="flex items-center gap-2 text-xs"
                  style={{ color: "var(--text-muted)" }}
                >
                  <CheckCircle2
                    className="w-3.5 h-3.5 flex-shrink-0"
                    style={{ color: "var(--primary)" }}
                  />
                  {req}
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="metric-card">
            <h4
              className="text-xs font-semibold uppercase tracking-widest mb-2"
              style={{ color: "var(--text-subtle)" }}
            >
              Notes
            </h4>
            <p className="text-xs leading-relaxed" style={{ color: "var(--text-muted)" }}>
              The CLI is optimised for local-first workflows and CI pipelines.
              Editable install mode keeps the command linked to your local source
              for rapid iteration during development.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
